import { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faFolderOpen, faNewspaper, faExclamationCircle, faClock, faCalendarAlt, faImage, faVideo, faPodcast, faFilePdf, faFile, faLayerGroup, faAlignLeft, faFileAlt, faPhotoVideo, faCode, faCalendar, faRotate, faShare, faCopy, faSpinner, faRobot, faTrash, faLink } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faXTwitter, faFacebookSquare } from '@fortawesome/free-brands-svg-icons';
import Tippy from '@tippyjs/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import GalleryBrowser from '../Galleries/GalleryBrowser';
import DocumentBrowser from '../Repository/DocumentBrowser';
import apiHelper from '../../utils/apiHelper';
import './ArticleForm.css';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PropTypes from 'prop-types';
import { newsService } from '../../services/newsService';
import React from 'react';

// Configure Quill Size attributor for custom font sizes
const Quill = ReactQuill.Quill;

// Use style-based attributors for both fonts and sizes (more reliable for persistence)
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10pt', '10.5pt', '12pt', '14pt', '18pt'];
Quill.register(SizeStyle, true);

const FontStyle = Quill.import('attributors/style/font');
FontStyle.whitelist = ['arial', 'times-new-roman', 'calibri', 'helvetica', 'georgia', 'verdana'];
Quill.register(FontStyle, true);

// Keep style-based for colors as they're more likely to be preserved
const ColorStyle = Quill.import('attributors/style/color');
ColorStyle.whitelist = [
    'rgb(102, 102, 102)', // Dark grey (default)
    '#000000', // Black
    '#e60000', // Red
    '#ff9900', // Orange
    '#ffcc00', // Yellow
    '#008a00', // Green
    '#0066cc', // Blue
    '#9933ff', // Purple
    '#ffffff', // White
    '#facccc', // Light red
    '#ffebcc', // Light orange
    '#ffffcc', // Light yellow
    '#cce8cc', // Light green
    '#cce0f5', // Light blue
    '#ebd6ff', // Light purple
    '#bbbbbb', // Light grey
    '#444444'  // Dark grey
];
Quill.register(ColorStyle, true);

// Add custom CSS for the font styles
const customCSS = `
/* Map inline font styles to proper font families */
.ql-editor [style*="font-family: arial"] { font-family: Arial, sans-serif !important; }
.ql-editor [style*="font-family: times-new-roman"] { font-family: "Times New Roman", serif !important; }
.ql-editor [style*="font-family: calibri"] { font-family: Calibri, sans-serif !important; }
.ql-editor [style*="font-family: helvetica"] { font-family: Helvetica, sans-serif !important; }
.ql-editor [style*="font-family: georgia"] { font-family: Georgia, serif !important; }
.ql-editor [style*="font-family: verdana"] { font-family: Verdana, sans-serif !important; }

/* Ensure font sizes display correctly in the editor */
.ql-editor [style*="font-size: 10pt"] { font-size: 10pt !important; }
.ql-editor [style*="font-size: 10.5pt"] { font-size: 10.5pt !important; }
.ql-editor [style*="font-size: 12pt"] { font-size: 12pt !important; }
.ql-editor [style*="font-size: 14pt"] { font-size: 14pt !important; }
.ql-editor [style*="font-size: 18pt"] { font-size: 18pt !important; }

/* Custom dropdown styling to show actual font names */
.ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
.ql-picker.ql-font .ql-picker-item[data-value="arial"]::before {
    content: 'Arial' !important;
}
.ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before,
.ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before {
    content: 'Times New Roman' !important;
}
.ql-picker.ql-font .ql-picker-label[data-value="calibri"]::before,
.ql-picker.ql-font .ql-picker-item[data-value="calibri"]::before {
    content: 'Calibri' !important;
}
.ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before,
.ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before {
    content: 'Helvetica' !important;
}
.ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
.ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before {
    content: 'Georgia' !important;
}
.ql-picker.ql-font .ql-picker-label[data-value="verdana"]::before,
.ql-picker.ql-font .ql-picker-item[data-value="verdana"]::before {
    content: 'Verdana' !important;
}

/* Custom dropdown styling to show actual font sizes */
.ql-picker.ql-size .ql-picker-label[data-value="10pt"]::before,
.ql-picker.ql-size .ql-picker-item[data-value="10pt"]::before {
    content: '10pt' !important;
}
.ql-picker.ql-size .ql-picker-label[data-value="10.5pt"]::before,
.ql-picker.ql-size .ql-picker-item[data-value="10.5pt"]::before {
    content: '10.5pt' !important;
}
.ql-picker.ql-size .ql-picker-label[data-value="12pt"]::before,
.ql-picker.ql-size .ql-picker-item[data-value="12pt"]::before {
    content: '12pt' !important;
}
.ql-picker.ql-size .ql-picker-label[data-value="14pt"]::before,
.ql-picker.ql-size .ql-picker-item[data-value="14pt"]::before {
    content: '14pt' !important;
}
.ql-picker.ql-size .ql-picker-label[data-value="18pt"]::before,
.ql-picker.ql-size .ql-picker-item[data-value="18pt"]::before {
    content: '18pt' !important;
}

/* No default font override - let ReactQuill handle it */

/* DatePicker Portal Styles */
#date-picker-portal {
    z-index: 9999 !important;
}

#date-picker-portal .react-datepicker {
    font-family: inherit;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    z-index: 9999 !important;
    background: white;
}

#date-picker-portal .react-datepicker__header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

#date-picker-portal .react-datepicker__day--selected {
    background-color: #007bff;
    color: white;
}

#date-picker-portal .react-datepicker__day:hover {
    background-color: #e9ecef;
}

/* Ensure the portal calendar appears above all modal content */
#date-picker-portal .react-datepicker-popper {
    z-index: 9999 !important;
}
`;

// Inject the CSS into the document head
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = customCSS;
    document.head.appendChild(styleSheet);

    // Create portal container for DatePicker if it doesn't exist
    if (!document.getElementById('date-picker-portal')) {
        const portalDiv = document.createElement('div');
        portalDiv.id = 'date-picker-portal';
        portalDiv.style.zIndex = '9999';
        document.body.appendChild(portalDiv);
    }
}

const MediaInput = ({ type, value, onBrowse, onClear, url, onChange, disabled }) => {
    const getIcon = () => {
        switch (type) {
            case 'image': return faImage;
            case 'video': return faVideo;
            case 'podcast': return faPodcast;
            case 'pdf': return faFilePdf;
            default: return faFile;
        }
    };

    const getLabel = () => {
        switch (type) {
            case 'image': return 'Featured Image';
            case 'video': return 'Video Content';
            case 'podcast': return 'Audio/Podcast';
            case 'pdf': return 'PDF Document';
            default: return 'File';
        }
    };

    const handleInputChange = (e) => {
        if (onChange) {
            onChange(e.target.value, type);
        }
    };

    // Display filename if selected from gallery, otherwise show manually entered URL
    const displayValue = value?.title || value?.name || value || url || '';

    return (
        <div className="media-preview-container">
            <label>
                <FontAwesomeIcon icon={getIcon()} />
                {getLabel()}
            </label>
            <div className="media-input">
                <input
                    type="text"
                    value={displayValue}
                    placeholder={`Enter URL or browse gallery for ${getLabel()}...`}
                    onChange={handleInputChange}
                    disabled={disabled}
                />
                <div className="media-buttons">
                    <Tippy content={`Browse ${getLabel()} gallery (upload & select)`}>
                        <button 
                            type="button" 
                            className="browse-button" 
                            onClick={onBrowse}
                            disabled={disabled}
                        >
                            <FontAwesomeIcon icon={faFolderOpen} />
                        </button>
                    </Tippy>
                    {(value || url) && (
                        <Tippy content="Clear selection">
                            <button 
                                type="button" 
                                className="clear-button" 
                                onClick={onClear}
                                disabled={disabled}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </Tippy>
                    )}
                </div>
            </div>
        </div>
    );
};

const DateInput = ({ id, name, value, onChange, min, disabled, label, error }) => {
    const handleChange = (date) => {
        if (date) {
            // Format date as YYYY-MM-DD in local timezone to avoid UTC conversion
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;

            onChange({
                target: {
                    name: name,
                    value: formattedDate
                }
            });
        } else {
            onChange({
                target: {
                    name: name,
                    value: ''
                }
            });
        }
    };

    // Create date object in local timezone to avoid timezone conversion
    const getSelectedDate = () => {
        if (!value) return null;

        // Parse YYYY-MM-DD format in local timezone
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split('-').map(Number);
            const result = new Date(year, month - 1, day); // month is 0-indexed
            return result;
        }

        return null;
    };

    return (
        <div className="form-group">
            <label htmlFor={id}>{label}</label>
            <div className="date-input-container">
                <DatePicker
                    id={id}
                    selected={getSelectedDate()}
                    onChange={handleChange}
                    dateFormat="dd/MM/yyyy"
                    minDate={min ? new Date(min) : null}
                    disabled={disabled}
                    className={error ? 'error' : ''}
                    placeholderText="Select or type date..."
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    withPortal
                    portalId="date-picker-portal"
                    popperPlacement="bottom-start"
                    popperModifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 5]
                            }
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                rootBoundary: 'viewport',
                                tether: false,
                                altAxis: true
                            }
                        },
                        {
                            name: 'flip',
                            options: {
                                behavior: ['bottom', 'top']
                            }
                        }
                    ]}
                />
                <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
            </div>
            {error && <span className="error-message">{error}</span>}
        </div>
    );
};

const ArticleFormComponent = React.memo(({ onClose, onSave, galleries, documentTypes, workingGroups, onUploadDocument, onFetchDocuments, onRefreshGalleries, weeklySections, initialData, isLoading, onRefreshNews }) => {
    const quillRef = useRef(null);
    const titleInputRef = useRef(null);
    // Move formatDateForInput function here so it can be used in initial state
    const formatDateForInput = (dateString) => {
        if (!dateString) {
            return new Date().toISOString().split('T')[0];
        }

        // Handle ISO string format (2024-01-01T00:00:00Z)
        if (dateString.includes('T')) {
            // Parse the ISO string and convert to local date
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        // If it's already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }

        // If it's in DD/MM/YYYY format
        const [day, month, year] = dateString.split('/');
        if (day && month && year) {
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }

        return new Date().toISOString().split('T')[0];
    };

    // declare all state variables
    const [formData, setFormData] = useState({
        id: initialData?.id || null,
        title: initialData?.title || '',
        content: initialData?.content || '',
        summary: initialData?.summary || '',
        status: initialData?.status || 'published',
        isFeatured: initialData?.isFeatured || false,
        featuredStartDate: initialData?.featuredStartDate ? formatDateForInput(initialData.featuredStartDate) : '',
        featuredEndDate: initialData?.featuredEndDate ? formatDateForInput(initialData.featuredEndDate) : '',
        featuredIndefinitely: initialData?.featuredIndefinitely || false,
        issueDate: initialData?.issueDate ? formatDateForInput(initialData.issueDate) : new Date().toISOString().split('T')[0],
        portalPublishDate: initialData?.portalPublishDate ? formatDateForInput(initialData.portalPublishDate) : new Date().toISOString().split('T')[0],
        weeklySection: initialData?.weeklySection || '',
        strategicCategory: initialData?.strategicCategory || '',
        author: initialData?.author || '',
        submittedAuthor: initialData?.submittedAuthor || '',
        submittedCompany: initialData?.submittedCompany || '',
        pdfName: initialData?.pdfName || '',
        imageName: initialData?.imageName || '',
        videoName: initialData?.videoName || '',
        podcastName: initialData?.podcastName || '',
        isWeeklyNews: Boolean(initialData?.isWeeklyNews) && !(initialData?.briefingDate || initialData?.BriefingDate),
        isSubmittedArticle: initialData?.isSubmittedArticle || false,
        isAudioBriefing: Boolean(initialData?.briefingDate || initialData?.BriefingDate),
        briefingDate: initialData?.briefingDate || initialData?.BriefingDate || '',
        // Add social media content fields
        SocialMediaPostLinkedIn: initialData?.SocialMediaPostLinkedIn || '',
        SocialMediaPostTwitterX: initialData?.SocialMediaPostTwitterX || '',
        SocialMediaPostFacebook: initialData?.SocialMediaPostFacebook || ''
    });

    // Then declare our new state for social content
    const [socialContent, setSocialContent] = useState('');

    const [showBrowser, setShowBrowser] = useState({
        image: false,
        video: false,
        podcast: false,
        pdf: false,
        link: false
    });

    const [errors, setErrors] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [itemExists, setItemExists] = useState(true);
    const [choiceFields, setChoiceFields] = useState({
        weeklyCategories: [],
        strategicCategories: []
    });

    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // Add with other state declarations
    const [showSocialModal, setShowSocialModal] = useState(false);

    // Add platform state
    const [selectedPlatform, setSelectedPlatform] = useState('linkedin');

    // Add generatingPlatform state to track which platform is generating
    const [generatingPlatform, setGeneratingPlatform] = useState(null);

    // Add documents state
    const [documents, setDocuments] = useState([]);

    // Add this with your other state declarations
    const [isSavingSocial, setIsSavingSocial] = useState(false);

    // Add generating summary state
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

    // Add deleting state
    const [isDeleting, setIsDeleting] = useState(false);

    // Add useEffect to auto-populate content for weekly news (new articles only)
    useEffect(() => {
        if (formData.isWeeklyNews && formData.title && quillRef.current && !initialData?.id) {
            // Use a timeout to debounce the title updates
            const timeoutId = setTimeout(() => {
                // Remember if the title input was focused
                const titleWasFocused = document.activeElement === titleInputRef.current;

                const quill = quillRef.current.getEditor();

                // If content is empty, add the title with proper formatting
                if (!formData.content.trim()) {
                    // Insert the title text
                    quill.setText(formData.title);

                    // Select all text and apply 12pt Arial formatting
                    quill.setSelection(0, formData.title.length);
                    quill.format('font', 'Arial');
                    quill.format('size', '12pt');
                    quill.format('bold', true);

                    // Move cursor to end and add a new line
                    quill.setSelection(formData.title.length);
                    quill.insertText(formData.title.length, '\nEnter content here...');

                    // Format the placeholder text as 10.5pt Arial
                    const totalLength = quill.getLength();
                    const placeholderStart = formData.title.length + 1; // +1 for the single newline
                    const placeholderEnd = totalLength - 1; // -1 to exclude the final newline

                    quill.setSelection(placeholderStart, placeholderEnd - placeholderStart);
                    quill.format('font', 'Arial');
                    quill.format('size', '10.5pt');
                    quill.format('bold', false); // Remove bold from placeholder

                    // Only set cursor in content if title wasn't focused
                    if (!titleWasFocused) {
                        quill.setSelection(placeholderStart);
                    }
                    // If title was focused, don't set any selection to avoid interfering with focus
                } else {
                    // Content exists - need to update the title
                    const content = quill.getText();
                    const lines = content.split('\n');

                    if (lines.length > 0) {
                        // Find the first line that looks like a title (non-empty first line)
                        const firstLine = lines[0];

                        // Replace the first line with the new title
                        const titleLength = firstLine.length;
                        quill.deleteText(0, titleLength);
                        quill.insertText(0, formData.title);

                        // Format the updated title as 12pt Arial bold
                        quill.setSelection(0, formData.title.length);
                        quill.format('font', 'Arial');
                        quill.format('size', '12pt');
                        quill.format('bold', true);

                        // Clear selection to avoid interfering with user input
                        // Only set selection if title wasn't focused to avoid stealing focus
                        if (!titleWasFocused) {
                            quill.setSelection(formData.title.length);
                        } else {
                            // If title was focused, clear selection without changing cursor position
                            quill.setSelection(formData.title.length, 0);
                        }
                    }
                }

                // Restore focus to title input if it was focused before
                if (titleWasFocused && titleInputRef.current) {
                    // Use setTimeout to ensure focus restoration happens after all Quill operations
                    setTimeout(() => {
                        if (titleInputRef.current) {
                            titleInputRef.current.focus();
                            // Restore cursor position to end of title
                            const titleLength = titleInputRef.current.value.length;
                            titleInputRef.current.setSelectionRange(titleLength, titleLength);
                        }
                    }, 10);
                }
            }, 500); // Wait 500ms after user stops typing

            // Cleanup timeout on unmount or dependency change
            return () => clearTimeout(timeoutId);
        }
    }, [formData.isWeeklyNews, formData.title, initialData?.id]);

    // Add useEffect to fetch documents if needed
    useEffect(() => {
        const loadDocuments = async () => {
            const docs = await onFetchDocuments();
            setDocuments(docs);
        };
        loadDocuments();
    }, [onFetchDocuments]);

    useEffect(() => {
        if (initialData) {
            // Only handle processing status and other non-date fields
            if (initialData.id && (initialData.isTemp || initialData.isProcessing)) {
                setIsProcessing(true);
                checkProcessingStatus(initialData.id);
            } else {
                setIsProcessing(false);
            }

            // Update any additional fields that aren't handled in initial state
            setFormData(prev => ({
                ...prev,
                submittedAuthor: initialData.SubmittedAuthor || initialData.submittedAuthor || prev.submittedAuthor,
                submittedCompany: initialData.Company || initialData.submittedCompany || prev.submittedCompany,
            }));
        }
    }, [initialData]);

    useEffect(() => {
        const loadChoiceFields = async () => {
            try {
                const choices = await newsService.getChoiceFields();
                setChoiceFields({
                    weeklyCategories: choices.weeklyCategories || [],
                    strategicCategories: choices.strategicCategories || []
                });
            } catch (error) {
                console.error('Failed to load choice fields:', error);
            }
        };

        loadChoiceFields();
    }, []);

    // 🔧 Set up default formatting for content
    useEffect(() => {
        if (quillRef.current) {
            setTimeout(() => {
                const quill = quillRef.current.getEditor();
                if (quill) {                
                    // Set default formatting for new content
                    quill.format('font', 'Arial', 'silent');
                    quill.format('size', '10.5pt', 'silent');
                }
            }, 500);
        }
    }, []);

    const checkProcessingStatus = async (articleId) => {
        if (!articleId) {
            setIsProcessing(false);
            return;
        }

        try {
            setIsCheckingStatus(true);
            const response = await apiHelper.get(`/api/graph/news/${articleId}/status`);

            if (response.data?.isProcessed !== undefined) {
                setIsProcessing(!response.data.isProcessed);

                if (!response.data.isProcessed) {
                    setTimeout(() => checkProcessingStatus(articleId), 5000);
                } else {
                    setIsCheckingStatus(false);
                    toast.success('Article processing completed');
                }
            } else {
                setIsProcessing(false);
                setIsCheckingStatus(false);
            }
        } catch (error) {
            setIsProcessing(false);
            setIsCheckingStatus(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        if (!formData.content.trim()) {
            newErrors.content = 'Content is required';
        }
        if (!formData.issueDate) {
            newErrors.issueDate = 'Issue Date is required';
        }
        if (!formData.portalPublishDate) {
            newErrors.portalPublishDate = 'Portal Publish Date is required';
        }

        // Only validate weekly section if it's a Weekly News article
        if (formData.isWeeklyNews && !formData.weeklySection) {
            newErrors.weeklySection = 'Weekly Section is required for Weekly News';
        }
        if (!formData.isFeatured && !formData.isWeeklyNews && !formData.isSubmittedArticle && !formData.isAudioBriefing) {
            newErrors.newsType = 'Please select at least one article type';
        }

        if (formData.isAudioBriefing && !formData.briefingDate?.trim()) {
            newErrors.briefingDate = 'Briefing Date is required for a Public Policy Audio Briefing';
        }

        if (formData.isSubmittedArticle) {
            if (!formData.submittedAuthor?.trim()) {
                newErrors.submittedAuthor = 'Author is required for submitted articles';
            }
            if (!formData.submittedCompany?.trim()) {
                newErrors.submittedCompany = 'Company is required for submitted articles';
            }
        }

        if (Object.keys(newErrors).length > 0) {
            document.querySelector('.validation-summary')?.scrollIntoView({ behavior: 'smooth' });
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const { user } = useAuth0();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setIsSaving(true);
                        
            const dataToSave = {
                ...formData,
                // Ensure status is properly capitalized for the server
                status: formData.status?.toLowerCase() === 'published' ? 'Published' : 'Draft',
                CreatedByStaff: user?.name || user?.email || 'Unknown User',
                // Explicitly include social media content
                SocialMediaPostLinkedIn: formData.SocialMediaPostLinkedIn || '',
                SocialMediaPostTwitterX: formData.SocialMediaPostTwitterX || '',
                SocialMediaPostFacebook: formData.SocialMediaPostFacebook || ''
            };
            
           
            const saveResult = await onSave(dataToSave);
            toast.success('Article saved successfully');
            
        } catch (error) {
            console.error('Error saving article:', error);
            toast.error('Failed to save article');
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (e) => {
        setIsDirty(true);
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCheckboxChange = (e) => {
        setIsDirty(true);
        const { name, checked } = e.target;

        if (initialData && (name === 'isWeeklyNews' || name === 'isSubmittedArticle' || name === 'isAudioBriefing')) {
            toast.error('Cannot change article type after creation');
            return;
        }

        // Handle featured checkbox and dates
        if (name === 'isFeatured') {
            setFormData(prev => ({
                ...prev,
                isFeatured: checked,
                // Clear featured dates if unchecking featured
                ...(checked ? {} : {
                    featuredStartDate: '',
                    featuredEndDate: '',
                    featuredIndefinitely: false
                })
            }));
            return;
        }

        // Handle featuredIndefinitely checkbox
        if (name === 'featuredIndefinitely') {
            setFormData(prev => ({
                ...prev,
                featuredIndefinitely: checked,
                // Clear end date if setting to indefinite
                ...(checked ? { featuredEndDate: '' } : {})
            }));
            return;
        }

        // For radio button behavior
        if (name === 'isWeeklyNews' && checked) {
            setFormData(prev => ({
                ...prev,
                isWeeklyNews: true,
                isSubmittedArticle: false,
                isAudioBriefing: false,
                briefingDate: '',
                submittedAuthor: '',
                submittedCompany: ''
            }));
        } else if (name === 'isSubmittedArticle' && checked) {
            setFormData(prev => ({
                ...prev,
                isWeeklyNews: false,
                isSubmittedArticle: true,
                isAudioBriefing: false,
                briefingDate: '',
                weeklySection: '',
                strategicCategory: ''
            }));
        } else if (name === 'isAudioBriefing' && checked) {
            setFormData(prev => ({
                ...prev,
                isWeeklyNews: false,
                isSubmittedArticle: false,
                isAudioBriefing: true,
                weeklySection: '',
                strategicCategory: '',
                submittedAuthor: '',
                submittedCompany: '',
                pdfName: '',
                pdfUrl: '',
                pdfId: ''
            }));
        }
    };

    const handleBrowseClick = (type) => {
       
        setShowBrowser(prev => {
            return { ...prev, [type]: true };
        });
    };

    const handleBrowserClose = (type) => {
        setShowBrowser(prev => ({ ...prev, [type]: false }));
    };

    const handleMediaSelect = (type, item) => {
        setFormData(prev => ({
            ...prev,
            ...(type === 'pdf' ? {
                pdfUrl: item.url || item.serverRelativeUrl || '',
                pdfName: item.fileName || item.name || item.title || '',
                pdfId: item.id || ''
            } : type === 'image' ? {
                imageUrl: item.url || item.serverRelativeUrl || '',
                imageName: item.name || item.title || '',
                imageId: item.id || ''
            } : type === 'video' ? {
                videoUrl: item.url || item.serverRelativeUrl || '',
                videoName: item.name || item.title || '',
                videoId: item.id || ''
            } : type === 'podcast' ? {
                podcastUrl: item.url || item.serverRelativeUrl || '',
                podcastName: item.name || item.title || '',
                podcastId: item.id || ''
            } : {})
        }));
        handleBrowserClose(type);
    };

    useEffect(() => {
        return () => {
            Object.keys(formData).forEach(key => {
                if (key.endsWith('Blob') && formData[key]) {
                    URL.revokeObjectURL(formData[key]);
                }
            });
        };
    }, []);

    const handleClearMedia = (type) => {
        setFormData(prev => {
            if (prev[`${type}Blob`]) {
                URL.revokeObjectURL(prev[`${type}Blob`]);
            }

            return {
                ...prev,
                [`${type}Url`]: '',
                [`${type}Name`]: '',
                [`${type}Type`]: '',
                [`${type}Id`]: '',
                [`${type}Blob`]: null
            };
        });
    };

    const handleYearChange = async (year) => {
        if (onFetchDocuments) {
            await onFetchDocuments(year);
        }
    };

    const handleDocumentUpload = async (formData) => {
        try {

            if (!onUploadDocument) {
                throw new Error('Upload handler not provided');
            }

            const result = await onUploadDocument(formData);

            if (!result) {
                throw new Error('Upload failed');
            }

            toast.success('Document uploaded successfully 1');
            return true;
        } catch (error) {
            console.error('Upload error in ArticleForm:', error);
            toast.error(error.message || 'Failed to upload document');
            throw error;
        }
    };

    const handleTypeChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            isWeeklyNews: value === 'weekly',
            isSubmittedArticle: value === 'submitted',
            isFeatured: false,
            weeklySection: '',
            strategicCategory: ''
        }));
    };

    // First, add this config before the ReactQuill component
    const quillModules = {
        toolbar: [
            [{ 'font': FontStyle.whitelist }],
            [{ 'size': SizeStyle.whitelist }],
            [{ 'color': ColorStyle.whitelist }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link'],
            ['clean']
        ],
        clipboard: {
            matchVisual: false // Prevents the deprecated mutation events
        }
    };

    const quillFormats = [
        'font', 'size', 'color',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'link'
    ];

    const handleOverlayClick = (e) => {
        // Prevent closing when clicking outside
        e.stopPropagation();
    };

    // Add this helper function to safely parse dates
    const parseDate = (dateString) => {
        if (!dateString) return null;

        // Try parsing ISO format
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            return date;
        }

        // Try parsing DD/MM/YYYY format
        const [day, month, year] = dateString.split('/');
        if (day && month && year) {
            return new Date(year, month - 1, day);
        }

        return null;
    };

    // Add this helper function to format dates consistently
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            return date.toISOString().split('T')[0];
        } catch (error) {
            console.error('Date formatting error:', error);
            return dateStr;
        }
    };

    // Add onRefreshGalleries to props
    const handleGalleriesRefresh = async () => {
        if (onRefreshGalleries) {
            await onRefreshGalleries(true);
        }
    };

    // Update the handleCopySocial function
    const handleCopySocial = (event) => {
        // Prevent any form submission or event bubbling
        event.preventDefault();
        event.stopPropagation();

        navigator.clipboard.writeText(socialContent);
        toast.success('Copied to clipboard!');
    };

    // Add link insertion functionality
    const handleInsertLink = () => {
        setShowBrowser(prev => ({ ...prev, link: true }));
    };

    const handleLinkSelect = (document) => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection(true); // true = focus the editor and get selection
                        
            // Get the document URL and name
            const fullUrl = document.url || document.serverRelativeUrl || '';
            const linkText = document.title || document.fileName || document.name || 'Document Link';
            const filename = document.fileName || document.name || '';
            
            // Phase 1: Use new format for existing articles, old format for new articles
            let linkUrl;
            let linkType;
            
            if (initialData?.id) {                
                const memberPortalUrl = process.env.REACT_APP_MEMBER_PORTAL_URL || 'https://memberportal.irishfunds.ie';
                const relativePath = `/files/detail/news/${initialData.id}/${encodeURIComponent(filename)}`;
                linkUrl = `${memberPortalUrl}${relativePath}`;
                linkType = 'news-document';

            } else {
                // New article - use old repository format (Phase 2 will handle this)
                linkUrl = filename ? `/assets/${filename}` : fullUrl;
                linkType = 'repository-document';
  
            }
            
           
            
            // Determine insertion point
            let insertIndex;
            if (range && range.index !== undefined) {
                // Insert at cursor position
                insertIndex = range.index;
               
            } else {
                // Insert at end if no cursor position
                insertIndex = quill.getLength() - 1;
                
            }
            
            // Create the link HTML based on the type with proper font styling
            let linkHtml;
            
            if (linkType === 'news-document') {
                // New format: Clean shareable link that works from emails with font styling
                linkHtml = `<a href="${linkUrl}" class="news-document-link" data-article-id="${initialData.id}" data-filename="${filename}" data-doc-title="${linkText}" style="font-family: arial; font-size: 10.5pt;">${linkText}</a>`;
            } else {
                // Old format: Repository document link with all the data attributes and font styling
                linkHtml = `<a href="${linkUrl}" class="repository-document-link" data-doc-id="${document.id || ''}" data-doc-title="${linkText}" data-doc-url="${fullUrl}" data-doc-filename="${filename}" data-full-url="${fullUrl}" style="font-family: arial; font-size: 10.5pt;">${linkText}</a>`;
            }
            
            try {
                // Insert the HTML directly at the specified position
                quill.clipboard.dangerouslyPasteHTML(insertIndex, linkHtml);
                
                // Set cursor after the inserted link
                const newPosition = insertIndex + linkText.length;
                quill.setSelection(newPosition, 0);
                
                toast.success('PDF inserted successfully!');
             
                
                
            } catch (error) {
                console.error('Failed to insert document link:', error);
                toast.error('Failed to insert document link');
            }
        }
        
        // Close the browser
        setShowBrowser(prev => ({ ...prev, link: false }));
    };

    // Update handleGenerateSocial function
    const handleGenerateSocial = async (platform) => {
        try {
            // Check if content already exists for this platform
            const existingContent = {
                linkedin: formData.SocialMediaPostLinkedIn,
                twitter: formData.SocialMediaPostTwitterX,
                facebook: formData.SocialMediaPostFacebook
            }[platform];

            if (existingContent) {
                // Show existing content without regenerating
                setSocialContent(existingContent);
                setSelectedPlatform(platform);
                setShowSocialModal(true);
                return;
            }

            // If no existing content, proceed with generation
            setGeneratingPlatform(platform);

            const result = await apiHelper.generateSocialContent(formData, platform);
            const content = result.data.socialContent || result.data;

            // Update formData with the generated content
            setFormData(prev => ({
                ...prev,
                ...(platform === 'linkedin' && { SocialMediaPostLinkedIn: content }),
                ...(platform === 'twitter' && { SocialMediaPostTwitterX: content }),
                ...(platform === 'facebook' && { SocialMediaPostFacebook: content })
            }));

            setSocialContent(content);
            setSelectedPlatform(platform);
            setShowSocialModal(true);

            toast.success('Social media content generated!');
        } catch (error) {
            console.error('Error generating social content:', error);
            toast.error('Failed to generate social content');
        } finally {
            setGeneratingPlatform(null);
        }
    };

    // Update handleRegenerateSocial to also update formData
    const handleRegenerateSocial = async () => {
        try {
            setGeneratingPlatform(selectedPlatform);

            const result = await apiHelper.generateSocialContent(formData, selectedPlatform);
            const content = result.data.socialContent || result.data;

            // Update formData with the regenerated content
            setFormData(prev => ({
                ...prev,
                ...(selectedPlatform === 'linkedin' && { SocialMediaPostLinkedIn: content }),
                ...(selectedPlatform === 'twitter' && { SocialMediaPostTwitterX: content }),
                ...(selectedPlatform === 'facebook' && { SocialMediaPostFacebook: content })
            }));

            setSocialContent(content);
            toast.success('Social media content regenerated!');
        } catch (error) {
            console.error('Error regenerating social content:', error);
            toast.error('Failed to regenerate social content');
        } finally {
            setGeneratingPlatform(null);
        }
    };

    // Add function to generate summary using Daire AI
    const handleGenerateSummary = async () => {
        setIsGeneratingSummary(true);
        try {
            // Check if we have content to summarize
            if (!formData.content || !formData.content.trim()) {
                throw new Error('No content available to summarize');
            }

            // Clean the HTML content to plain text by stripping HTML tags
            const cleanedContent = formData.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            
            if (!cleanedContent.trim()) {
                throw new Error('No meaningful content found to summarize');
            }

            // Get headers from apiHelper for Daire API
            const headers = await apiHelper.getHeaders('daire');

            // Call the same Daire API endpoint used in DocumentDetailsModal
            const aiResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/daire/summariseContent`, {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text: cleanedContent,
                    title: formData.title || 'News Article',
                    type: "News Article"
                })
            });

            if (!aiResponse.ok) {
                throw new Error(`Failed to generate summary: ${aiResponse.status} ${aiResponse.statusText}`);
            }

            const data = await aiResponse.json();

            // Extract the summary from the AI response
            const generatedSummary = data.summary?.choices?.[0]?.message?.content;
            
            if (!generatedSummary) {
                throw new Error('No summary was generated by the AI service');
            }

            // Update the form data with the generated summary
            setFormData(prev => ({
                ...prev,
                summary: generatedSummary
            }));

            toast.success('Summary generated successfully!');
        } catch (error) {
            console.error('Summary generation error:', error);
            toast.error('Failed to generate summary: ' + error.message);
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    // Add delete article handler
    const handleDeleteArticle = async () => {
        if (!initialData?.id) {
            toast.error('Cannot delete article: No article ID found');
            return;
        }

        const confirmMessage = `Are you sure you want to delete "${formData.title}"?\n\nThis action cannot be undone and will close the form.`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setIsDeleting(true);
          
            
            // Call the delete API with the article type
            await newsService.deleteArticle(initialData.id, formData.isSubmittedArticle);
            
            // Show success message
            toast.success('Article deleted successfully');
            
            // Refresh the news list in the parent component
            if (onRefreshNews) {
                await onRefreshNews();
            }
            
            // Close the form since the article no longer exists
            onClose();
            
        } catch (error) {
            console.error('Error deleting article:', error);
            toast.error('Failed to delete article: ' + (error.message || 'Unknown error'));
        } finally {
            setIsDeleting(false);
        }
    };

    // Add useEffect to force 12pt styling after content changes
    useEffect(() => {
        if (formData.isWeeklyNews && formData.content) {
            // Use setTimeout to ensure DOM is updated
            setTimeout(() => {
                const editor = document.querySelector('.ql-editor');
                if (editor) {
                    // Only apply styling if the content doesn't already have the required styling
                    const hasRequiredStyling = editor.querySelector('p[style*="12pt"]') || 
                                            editor.querySelector('.ql-size-12pt');
                    
                    if (!hasRequiredStyling) {
                        // Look for both inline styles and class-based sizing
                        const paragraphsWithStyle = editor.querySelectorAll('p[style*="12pt"]');
                        const elementsWithClass = editor.querySelectorAll('.ql-size-12pt');
                        
                        // Process both types
                        [...paragraphsWithStyle, ...elementsWithClass].forEach((element) => {
                            element.style.setProperty('font-size', '12pt', 'important');
                            element.style.setProperty('font-weight', 'bold', 'important');
                        });
                    }
                }
            }, 100);
        }
    }, [formData.content, formData.isWeeklyNews]);

    // Add useEffect to ensure font sizes display correctly in the editor
    useEffect(() => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            
            // Force refresh of editor display
            const refreshEditor = () => {
                setTimeout(() => {
                    const editor = document.querySelector('.ql-editor');
                    if (editor) {
                        // Force a repaint by temporarily changing a style
                        const originalDisplay = editor.style.display;
                        editor.style.display = 'none';
                        // eslint-disable-next-line no-unused-vars
                        const height = editor.offsetHeight; // Trigger reflow
                        editor.style.display = originalDisplay;
                    }
                }, 50);
            };

            // Function to apply default font to elements without font-family
            const applyDefaultFont = () => {
                setTimeout(() => {
                    const editor = document.querySelector('.ql-editor');
                    if (editor) {
                        // Find all elements that have font-size but no font-family
                        const elementsWithSize = editor.querySelectorAll('[style*="font-size"]');
                        elementsWithSize.forEach(element => {
                            const style = element.getAttribute('style') || '';
                            if (style.includes('font-size') && !style.includes('font-family')) {
                                // Add Arial as default font-family
                                const newStyle = style + '; font-family: arial';
                                element.setAttribute('style', newStyle);
                            }
                        });
                        
                        // Also handle elements without any style but with content
                        const allTextElements = editor.querySelectorAll('p, span, strong, em, u, s, li, h1, h2, h3, h4, h5, h6');
                        allTextElements.forEach(element => {
                            const style = element.getAttribute('style') || '';
                            if (!style.includes('font-family') && element.textContent.trim()) {
                                // Add default font-family and size if missing
                                let newStyle = style;
                                if (!style.includes('font-family')) {
                                    newStyle += (newStyle ? '; ' : '') + 'font-family: arial';
                                }
                                if (!style.includes('font-size')) {
                                    newStyle += (newStyle ? '; ' : '') + 'font-size: 10.5pt';
                                }
                                if (newStyle !== style) {
                                    element.setAttribute('style', newStyle);
                                }
                            }
                        });
                    }
                }, 100);
            };

            // Listen for text changes that might include font size changes
            quill.on('text-change', () => {
                refreshEditor();
                applyDefaultFont();
            });
            
            // Listen for selection changes to update toolbar display
            quill.on('selection-change', (range) => {
                if (range) {
                    const format = quill.getFormat(range);

                    
                    // Force toolbar to update its display
                    setTimeout(() => {
                        const toolbar = document.querySelector('.ql-toolbar');
                        if (toolbar) {
                            // Trigger toolbar update
                            const sizeButton = toolbar.querySelector('.ql-size .ql-picker-label');
                            if (sizeButton && format.size) {
                                sizeButton.setAttribute('data-value', format.size);
                            }
                            const fontButton = toolbar.querySelector('.ql-font .ql-picker-label');
                            if (fontButton && format.font) {
                                fontButton.setAttribute('data-value', format.font);
                            }
                        }
                    }, 10);
                }
            });

            // Apply default font on initial load
            applyDefaultFont();

            return () => {
                quill.off('text-change');
                quill.off('selection-change');
            };
        }
    }, [quillRef.current]);

    return (
        <>
            <div className="modal-overlay" onClick={handleOverlayClick}>
                <div className="new-article-form" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>
                            <FontAwesomeIcon icon={faNewspaper} />
                            {initialData ? 'Edit Article' : 'New Article'}
                        </h2>
                        <button className="close-button" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    {isProcessing && (
                        <div className="processing-status">
                            <div className="spinner"></div>
                            <span>Processing article... Please wait</span>
                        </div>
                    )}

                    {initialData?.id && !itemExists && (
                        <div className="alert alert-warning">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            This news item no longer exists in SharePoint. Editing is disabled.
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">



                            {/* Article basics section */}
                            <div className="form-section">
                                <div className="form-group">
                                    <label htmlFor="title">Title*</label>
                                    <input
                                        type="text"
                                        id="title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        className={errors.title ? 'error' : ''}
                                        placeholder="Enter article title"
                                        disabled={initialData?.id && !itemExists}
                                        ref={titleInputRef}
                                    />
                                    {errors.title && <span className="error-message">{errors.title}</span>}
                                </div>
                            </div>

                           


                            {/* News Type section - only show for new articles */}
                            {!initialData?.id && (
                                <div className="form-section">
                                    <div className="form-group">
                                        <label className="section-label">News Type*</label>
                                        <div className="radio-group">
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="isWeeklyNews"
                                                    checked={formData.isWeeklyNews}
                                                    onChange={handleCheckboxChange}
                                                />
                                                News
                                            </label>
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="isSubmittedArticle"
                                                    checked={formData.isSubmittedArticle}
                                                    onChange={handleCheckboxChange}
                                                />
                                                Submitted Article
                                            </label>
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="isAudioBriefing"
                                                    checked={formData.isAudioBriefing}
                                                    onChange={handleCheckboxChange}
                                                />
                                                Public Policy Audio Briefing
                                            </label>
                                        </div>
                                        <div className="featured-controls">
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    name="isFeatured"
                                                    checked={formData.isFeatured}
                                                    onChange={handleCheckboxChange}
                                                />
                                                Feature this article
                                            </label>

                                            {formData.isFeatured && (
                                                <div className="featured-dates-grid">
                                                    <div className="date-field">
                                                        <label>Featured Start Date</label>
                                                        <DateInput
                                                            id="featuredStartDate"
                                                            name="featuredStartDate"
                                                            value={formData.featuredStartDate}
                                                            onChange={handleChange}
                                                            error={errors.featuredStartDate}
                                                        />
                                                    </div>
                                                    <div className="date-field">
                                                        <label>Featured End Date</label>
                                                        <DateInput
                                                            id="featuredEndDate"
                                                            name="featuredEndDate"
                                                            value={formData.featuredEndDate}
                                                            onChange={handleChange}
                                                            error={errors.featuredEndDate}
                                                            disabled={formData.featuredIndefinitely}
                                                        />
                                                    </div>
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            name="featuredIndefinitely"
                                                            checked={formData.featuredIndefinitely}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        Feature Indefinitely
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Featured section for existing articles */}
                            {initialData?.id && (
                                <div className="form-section">
                                    <div className="form-group">
                                        <div className="featured-controls">
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    name="isFeatured"
                                                    checked={formData.isFeatured}
                                                    onChange={handleCheckboxChange}
                                                    disabled={initialData?.id && !itemExists}
                                                />
                                                Feature this article
                                            </label>

                                            {formData.isFeatured && (
                                                <div className="featured-dates-grid">
                                                    <div className="date-field">
                                                        <label>Featured Start Date</label>
                                                        <DateInput
                                                            id="featuredStartDate"
                                                            name="featuredStartDate"
                                                            value={formData.featuredStartDate}
                                                            onChange={handleChange}
                                                            error={errors.featuredStartDate}
                                                        />
                                                    </div>
                                                    <div className="date-field">
                                                        <label>Featured End Date</label>
                                                        <DateInput
                                                            id="featuredEndDate"
                                                            name="featuredEndDate"
                                                            value={formData.featuredEndDate}
                                                            onChange={handleChange}
                                                            error={errors.featuredEndDate}
                                                            disabled={formData.featuredIndefinitely}
                                                        />
                                                    </div>
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            name="featuredIndefinitely"
                                                            checked={formData.featuredIndefinitely}
                                                            onChange={handleCheckboxChange}
                                                            disabled={initialData?.id && !itemExists}
                                                        />
                                                        Feature Indefinitely
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Submitted Article fields */}
                            {formData.isSubmittedArticle && (
                                <div className="form-section">
                                    <div className="form-group">
                                        <label>Author*</label>
                                        <input
                                            type="text"
                                            name="submittedAuthor"
                                            value={formData.submittedAuthor}
                                            onChange={handleChange}
                                            className={errors.submittedAuthor ? 'error' : ''}
                                            disabled={initialData?.id && !itemExists}
                                        />
                                        {errors.submittedAuthor && <span className="error-message">{errors.submittedAuthor}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Company*</label>
                                        <input
                                            type="text"
                                            name="submittedCompany"
                                            value={formData.submittedCompany}
                                            onChange={handleChange}
                                            className={errors.submittedCompany ? 'error' : ''}
                                            disabled={initialData?.id && !itemExists}
                                        />
                                        {errors.submittedCompany && <span className="error-message">{errors.submittedCompany}</span>}
                                    </div>
                                </div>
                            )}

                            {/* Public Policy Audio Briefing — free-text briefing date */}
                            {formData.isAudioBriefing && (
                                <div className="form-section">
                                    <div className="form-group">
                                        <label htmlFor="briefingDate">Briefing Date*</label>
                                        <input
                                            type="text"
                                            id="briefingDate"
                                            name="briefingDate"
                                            value={formData.briefingDate}
                                            onChange={handleChange}
                                            className={errors.briefingDate ? 'error' : ''}
                                            placeholder={'e.g. "latest" or 2026-07-01'}
                                            disabled={initialData?.id && !itemExists}
                                        />
                                        <small className="field-hint">
                                            Enter the applicable code from your audio provider. This will embed the audio briefing into the news item.
                                        </small>
                                        {errors.briefingDate && <span className="error-message">{errors.briefingDate}</span>}
                                    </div>
                                </div>
                            )}

                            {/* Content section */}
                            <div className="form-section">
                                <div className="form-group">
                                    <label htmlFor="content">Content*</label>
                                    <div className="content-container" style={{ position: 'relative' }}>
                                        <ReactQuill
                                            value={formData.content}
                                            onChange={(content) => {
                                              
                                                
                                                setFormData(prev => ({
                                                    ...prev,
                                                    content
                                                }));
                                                if (errors.content) {
                                                    setErrors(prev => ({ ...prev, content: '' }));
                                                }
                                            }}
                                            onSelectionChange={(range, source, editor) => {
                                                // Debug: Log current formatting when selection changes
                                                if (range && quillRef.current) {
                                                    const quill = quillRef.current.getEditor();
                                                    const format = quill.getFormat(range);
                                  
                                                }
                                            }}
                                            modules={quillModules}
                                            formats={quillFormats}
                                            className={errors.content ? 'error' : ''}
                                            readOnly={initialData?.id && !itemExists}
                                            placeholder="Start typing your content..."
                                            style={{ fontSize: '10.5pt' }}
                                            ref={quillRef}
                                        />
                                        <button
                                            type="button"
                                            className="insert-link-btn"
                                            onClick={handleInsertLink}
                                            disabled={initialData?.id && !itemExists}
                                            title={initialData?.id ? 
                                                "Insert shareable document link (will work from emails)" :
                                                "Insert repository document link"
                                            }
                                            
                                            onMouseEnter={(e) => {
                                                if (!(initialData?.id && !itemExists)) {
                                                    e.target.style.backgroundColor = '#0056b3';
                                                    e.target.style.transform = 'translateY(-1px)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!(initialData?.id && !itemExists)) {
                                                    e.target.style.backgroundColor = '#007bff';
                                                    e.target.style.transform = 'translateY(0)';
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faFilePdf} />
                                            Insert PDF
                                        </button>
                                    </div>
                                    {errors.content && <span className="error-message">{errors.content}</span>}
                                </div>
                            </div>

                            {/* Summary section */}
                            <div className="form-section">
                                <div className="form-group">
                                    <label htmlFor="summary">Summary</label>
                                    <div className="summary-container">
                                        <textarea
                                            id="summary"
                                            name="summary"
                                            value={formData.summary}
                                            onChange={handleChange}
                                            className={errors.summary ? 'error' : ''}
                                            placeholder="Enter an optional brief summary of the article..."
                                            disabled={initialData?.id && !itemExists}
                                            rows="4"
                                            style={{ resize: 'vertical', minHeight: '100px' }}
                                        />
                                        {formData.content && formData.content.trim() && (
                                            <button
                                                type="button"
                                                className="generate-summary-btn"
                                                onClick={handleGenerateSummary}
                                                disabled={isGeneratingSummary || (initialData?.id && !itemExists)}
                                                title={formData.summary ?
                                                    "Regenerate summary using AI based on the article content" :
                                                    "Generate summary using AI based on the article content"
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={isGeneratingSummary ? faSpinner : faRobot}
                                                    spin={isGeneratingSummary}
                                                />
                                                {isGeneratingSummary ? 'Generating...' :
                                                    formData.summary ? 'Regenerate Summary with Daire' : 'Generate Summary with Daire'}
                                            </button>
                                        )}
                                    </div>
                                    {errors.summary && <span className="error-message">{errors.summary}</span>}
                                </div>
                            </div>

                            {/* Article metadata section */}
                            <div className="form-section">
                                <div className="form-row">
                                    <div className="form-group">
                                        <DateInput
                                            id="issueDate"
                                            name="issueDate"
                                            value={formData.issueDate}
                                            onChange={handleChange}
                                            error={errors.issueDate}
                                            disabled={initialData?.id && !itemExists}
                                            label="Issue Date"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <DateInput
                                            id="portalPublishDate"
                                            name="portalPublishDate"
                                            value={formData.portalPublishDate}
                                            onChange={handleChange}
                                            error={errors.portalPublishDate}
                                            disabled={initialData?.id && !itemExists}
                                            label="Portal Publish Date"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select
                                            name="status"
                                            value={formData.status?.toLowerCase()}
                                            onChange={handleChange}
                                            disabled={initialData?.id && !itemExists}
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="published">Published</option>
                                        </select>
                                    </div>
                                    {formData.isWeeklyNews && (
                                        <div className="form-group">
                                            <label>Weekly Section*</label>
                                            <select
                                                name="weeklySection"
                                                value={formData.weeklySection}
                                                onChange={handleChange}
                                                className={errors.weeklySection ? 'error' : ''}
                                                disabled={initialData?.id && !itemExists}
                                            >
                                                <option value="">Select Weekly Section</option>
                                                {choiceFields.weeklyCategories.map(section => (
                                                    <option key={section} value={section}>
                                                        {section}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.weeklySection && <span className="error-message">{errors.weeklySection}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>



                            <div className="form-section">
                                <div className="social-media-section">
                                    <h3>Social Media</h3>
                                    <div className="platform-buttons">
                                        <button
                                            type="button"
                                            onClick={() => handleGenerateSocial('linkedin')}
                                            className={`btn-platform ${selectedPlatform === 'linkedin' ? 'active' : ''} ${formData.SocialMediaPostLinkedIn ? 'has-content' : ''}`}
                                            disabled={generatingPlatform !== null || !formData.content}
                                            data-platform="linkedin"
                                            title="Generate LinkedIn post"
                                        >
                                            {generatingPlatform === 'linkedin' ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faLinkedin} /> LinkedIn {initialData?.SocialMediaPostLinkedIn ? '(Saved)' : formData.SocialMediaPostLinkedIn ? '(Draft)' : ''}</>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleGenerateSocial('twitter')}
                                            className={`btn-platform ${selectedPlatform === 'twitter' ? 'active' : ''} ${formData.SocialMediaPostTwitterX ? 'has-content' : ''}`}
                                            disabled={generatingPlatform !== null || !formData.content}
                                            data-platform="twitter"
                                            title="Generate X/Twitter post"
                                        >
                                            {generatingPlatform === 'twitter' ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faXTwitter} /> X/Twitter {initialData?.SocialMediaPostTwitterX ? '(Saved)' : formData.SocialMediaPostTwitterX ? '(Draft)' : ''}</>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleGenerateSocial('facebook')}
                                            className={`btn-platform ${selectedPlatform === 'facebook' ? 'active' : ''} ${formData.SocialMediaPostFacebook ? 'has-content' : ''}`}
                                            disabled={generatingPlatform !== null || !formData.content}
                                            data-platform="facebook"
                                            title="Generate Facebook post"
                                        >
                                            {generatingPlatform === 'facebook' ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faFacebookSquare} /> Facebook {initialData?.SocialMediaPostFacebook ? '(Saved)' : formData.SocialMediaPostFacebook ? '(Draft)' : ''}</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>


                            <div className="form-section media-section">
                                <h3>Media Selection</h3>

                                <div className="form-group">
                                    <label>Featured Image</label>
                                    <MediaInput
                                        type="image"
                                        value={formData.imageName}
                                        url={formData.imageUrl}
                                        onBrowse={() => handleBrowseClick('image')}
                                        onClear={() => handleClearMedia('image')}
                                        onChange={(value, type, metadata) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                [`${type}Url`]: value,
                                                [`${type}Name`]: metadata?.name || metadata?.title || value,
                                                [`${type}Source`]: metadata?.source || 'manual'
                                            }));
                                        }}
                                        disabled={initialData?.id && !itemExists}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Video</label>
                                    <MediaInput
                                        type="video"
                                        value={formData.videoName}
                                        url={formData.videoUrl}
                                        onBrowse={() => handleBrowseClick('video')}
                                        onClear={() => handleClearMedia('video')}
                                        onChange={(value, type, metadata) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                [`${type}Url`]: value,
                                                [`${type}Name`]: metadata?.name || metadata?.title || value,
                                                [`${type}Source`]: metadata?.source || 'manual'
                                            }));
                                        }}
                                        disabled={initialData?.id && !itemExists}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Audio</label>
                                    <MediaInput
                                        type="podcast"
                                        value={formData.podcastName}
                                        url={formData.podcastUrl}
                                        onBrowse={() => handleBrowseClick('podcast')}
                                        onClear={() => handleClearMedia('podcast')}
                                        onChange={(value, type, metadata) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                [`${type}Url`]: value,
                                                [`${type}Name`]: metadata?.name || metadata?.title || value,
                                                [`${type}Source`]: metadata?.source || 'manual'
                                            }));
                                        }}
                                        disabled={initialData?.id && !itemExists}
                                    />
                                </div>

                                {/* PDF is not available for Public Policy Audio Briefings */}
                                {!formData.isAudioBriefing && (
                                    <div className="form-group">
                                        <label>PDF Document</label>
                                        <MediaInput
                                            type="pdf"
                                            value={formData.pdfName}
                                            url={formData.pdfUrl}
                                            onBrowse={() => handleBrowseClick('pdf')}
                                            onClear={() => handleClearMedia('pdf')}
                                            onChange={(value, type, metadata) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    [`${type}Url`]: value,
                                                    [`${type}Name`]: metadata?.name || metadata?.title || value,
                                                    [`${type}Source`]: metadata?.source || 'manual'
                                                }));
                                            }}
                                            disabled={initialData?.id && !itemExists}
                                        />
                                    </div>
                                )}
                            </div>

                
                        </div>

                        <div className="modal-footer">
                            <div className="footer-buttons" style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {/* Removed indexing section */}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {Object.keys(errors).length > 0 && !errors.submit && (
                                        <div className="footer-validation-summary">
                                            <FontAwesomeIcon icon={faExclamationCircle} className="error-icon" />
                                            <span>Validation Errors, please check</span>
                                        </div>
                                    )}
                                    <button type="button" className="btn-secondary" onClick={onClose}>
                                        Close
                                    </button>
                                    {initialData?.id && (
                                        <button
                                            type="button"
                                            className="btn-danger"
                                            onClick={handleDeleteArticle}
                                            disabled={isDeleting}
                                            title={isDeleting ? "Deleting article..." : "Delete this article permanently"}
                                            style={{
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: '1px solid #dc3545',
                                                padding: '8px 16px',
                                                borderRadius: '4px',
                                                cursor: isDeleting ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '0.95rem',
                                                fontWeight: '500',
                                                transition: 'all 0.2s ease',
                                                opacity: isDeleting ? 0.7 : 1
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isDeleting) {
                                                    e.target.style.backgroundColor = '#c82333';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isDeleting) {
                                                    e.target.style.backgroundColor = '#dc3545';
                                                }
                                            }}
                                        >
                                            {isDeleting ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Deleting...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faTrash} /> Delete</>
                                            )}
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={isSaving || isProcessing || (initialData?.id && !itemExists)}
                                        title={
                                            isSaving ? "Saving..." :
                                            initialData?.id && !itemExists ? "This item no longer exists" :
                                            `Save ${formData.isAudioBriefing ? "Audio Briefing" : formData.isWeeklyNews ? "News" : "Submitted Article"}`
                                        }
                                    >
                                        {isSaving ? (
                                            <><FontAwesomeIcon icon={faClock} spin /> Saving...</>
                                        ) : isCheckingStatus ? (
                                            <><FontAwesomeIcon icon={faClock} spin /> Processing...</>
                                        ) : (
                                            <><FontAwesomeIcon icon={faSave} /> Save {formData.isAudioBriefing ? "Audio Briefing" : formData.isWeeklyNews ? "News" : "Submitted Article"}</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {showBrowser.image && (
                <GalleryBrowser
                    galleries={galleries.filter(g => g.type?.toLowerCase() === 'image')}
                    mediaType="image"
                    onSelect={(item) => handleMediaSelect('image', item)}
                    onClose={() => handleBrowserClose('image')}
                    onRefreshGalleries={onRefreshGalleries}
                />
            )}
            {showBrowser.video && (
                <GalleryBrowser
                    galleries={galleries.filter(g => g.type?.toLowerCase() === 'video')}
                    mediaType="video"
                    onSelect={(item) => handleMediaSelect('video', item)}
                    onClose={() => handleBrowserClose('video')}
                    onRefreshGalleries={onRefreshGalleries}
                />
            )}
            {showBrowser.podcast && (
                <GalleryBrowser
                    galleries={galleries.filter(g => g.type?.toLowerCase() === 'podcast')}
                    mediaType="podcast"
                    onSelect={(item) => handleMediaSelect('podcast', item)}
                    onClose={() => handleBrowserClose('podcast')}
                    onRefreshGalleries={onRefreshGalleries}
                />
            )}
            {showBrowser.pdf && (
                <DocumentBrowser
                    documents={documents}
                    documentTypes={documentTypes || []}
                    workingGroups={workingGroups || []}
                    onSelect={(doc) => handleMediaSelect('pdf', doc)}
                    onClose={() => handleBrowserClose('pdf')}
                    onUpload={handleDocumentUpload}
                    onYearChange={handleYearChange}
                    onRefresh={async (docs, year) => {
                        if (onFetchDocuments) {
                            await onFetchDocuments(year);
                        }
                    }}
                    isLoading={isLoading}
                />
            )}
            {showBrowser.link && (
                <DocumentBrowser
                    documents={documents}
                    documentTypes={documentTypes || []}
                    workingGroups={workingGroups || []}
                    onSelect={handleLinkSelect}
                    onClose={() => handleBrowserClose('link')}
                    onUpload={handleDocumentUpload}
                    onYearChange={handleYearChange}
                    onRefresh={async (docs, year) => { 
                        if (onFetchDocuments) {
                            await onFetchDocuments(year);
                        }
                    }}
                    isLoading={isLoading}
                    title="Select Document to Insert as Link"
                />
            )}

            {socialContent && showSocialModal && (
                <div className="modal-overlay">
                    <div className="social-content-modal">
                        <div className="modal-header">
                            <h2>
                                <FontAwesomeIcon icon={faShare} />
                                {selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1)} Post
                            </h2>
                            <div className="modal-header-buttons">
                                <button
                                    onClick={() => setShowSocialModal(false)}
                                    className="close-button"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        </div>
                        <div className="modal-body">
                            <div className={`social-preview ${selectedPlatform}`}>
                                <div className="platform-icon">
                                    <FontAwesomeIcon
                                        icon={
                                            selectedPlatform === 'linkedin' ? faLinkedin :
                                                selectedPlatform === 'twitter' ? faXTwitter :
                                                    faFacebookSquare
                                        }
                                        size="2x"
                                    />
                                </div>
                                <div className="social-content">
                                    {socialContent.split('\n').map((line, index) => {
                                        // Handle bold text
                                        const formattedLine = line.replace(
                                            /\*\*(.*?)\*\*/g,
                                            '<strong>$1</strong>'
                                        );

                                        return (
                                            <p
                                                key={index}
                                                dangerouslySetInnerHTML={{ __html: formattedLine }}
                                                className={line.trim().startsWith('**') ? 'bold-line' : ''}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="footer-buttons">
                                <button
                                    onClick={() => setShowSocialModal(false)}
                                    className="btn-secondary"
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faTimes} /> Close
                                </button>
                                <button
                                    onClick={handleRegenerateSocial}
                                    className="btn-secondary btn-regenerate"
                                    disabled={generatingPlatform !== null}
                                    type="button"
                                    title="Regenerate social media content"
                                >
                                    {generatingPlatform === selectedPlatform ? (
                                        <><FontAwesomeIcon icon={faSpinner} spin /> Regenerating...</>
                                    ) : (
                                        <><FontAwesomeIcon icon={faRotate} /> Regenerate</>
                                    )}
                                </button>
                                <button
                                    onClick={(e) => handleCopySocial(e)}
                                    className="btn-secondary"
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faCopy} /> Copy
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});

ArticleFormComponent.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    galleries: PropTypes.array.isRequired,
    documentTypes: PropTypes.array,
    workingGroups: PropTypes.array,
    onUploadDocument: PropTypes.func.isRequired,
    onFetchDocuments: PropTypes.func.isRequired,
    onRefreshGalleries: PropTypes.func.isRequired,
    weeklySections: PropTypes.array.isRequired,
    initialData: PropTypes.object,
    isLoading: PropTypes.bool,
    onRefreshNews: PropTypes.func
};

ArticleFormComponent.defaultProps = {
    galleries: [],
    documentTypes: [],
    workingGroups: [],
    weeklySections: [],
    onUploadDocument: () => { },
    onFetchDocuments: () => { },
    onRefreshGalleries: () => { },
    isLoading: false
};

const WrappedComponent = withAuthenticationRequired(ArticleFormComponent, {
    onRedirecting: () => (
        <div className="loading-spinner">
            Loading...
        </div>
    ),
});

export default WrappedComponent;